import { useCallback, useEffect, useMemo, useState } from 'react'
import { Info, Loader2, Save } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { Button } from '@/components/ui/button'
import { Input, Textarea, Select, Field } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { useToast } from '@/providers/UiProviders'
import {
  fetchSystemConfig,
  updateSystemConfig,
  coerceValue,
  SYS_STR as S,
  type ConfigSection,
  type ConfigValue,
  type FieldSpec,
} from './systemConfigApi'

/**
 * Reusable A24 config form. Reads/writes the REAL `/admin/settings/system`
 * key/value store; each `FieldSpec` maps to one app_settings key. Fields whose
 * keys are not yet returned fall back to their `def` (greenfield fail-soft) and
 * persist once the backend struct is extended. No fake toggles — every field is
 * a genuine platform setting, only its wiring is incremental.
 */
export function SystemConfigForm({
  screenId,
  title,
  description,
  sections,
}: {
  screenId: string
  title: string
  description?: string
  sections: ConfigSection[]
}) {
  const { toast } = useToast()
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [saving, setSaving] = useState(false)

  const allFields = useMemo<FieldSpec[]>(() => sections.flatMap((s) => s.fields), [sections])

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true)
      setError(false)
      fetchSystemConfig(signal)
        .then((cfg) => {
          const next: Record<string, string> = {}
          for (const f of allFields) {
            if (f.secret) {
              next[f.key] = '' // secrets are never pre-filled
            } else {
              next[f.key] = cfg[f.key] ?? f.def ?? ''
            }
          }
          setValues(next)
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false))
    },
    [allFields],
  )

  useEffect(() => {
    const ac = new AbortController()
    load(ac.signal)
    return () => ac.abort()
  }, [load])

  const patch = (key: string, v: string) => setValues((prev) => ({ ...prev, [key]: v }))

  const save = useCallback(async () => {
    setSaving(true)
    try {
      const body: Record<string, ConfigValue> = {}
      for (const f of allFields) {
        const raw = values[f.key] ?? ''
        if (f.secret && raw.trim() === '') continue // don't overwrite unchanged secret
        body[f.key] = coerceValue(f, raw)
      }
      await updateSystemConfig(body)
      toast(S.saved, { tone: 'success' })
    } catch {
      toast(S.saveError, { tone: 'error' })
    } finally {
      setSaving(false)
    }
  }, [allFields, values, toast])

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={title}
        screenId={screenId}
        description={description}
        actions={
          <Button variant="primary" onClick={save} disabled={saving || loading || error}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? S.saving : S.save}
          </Button>
        }
      />

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-secondary">
            <Loader2 size={15} className="animate-spin" /> {S.loading}
          </div>
        ) : error ? (
          <div className="flex flex-col items-start gap-2 text-sm text-danger">
            {S.loadError}
            <Button variant="ghost" onClick={() => load()}>
              {S.retry}
            </Button>
          </div>
        ) : (
          <div className="mx-auto flex max-w-2xl flex-col gap-4">
            <div className="flex items-start gap-2 rounded-md border border-border-subtle bg-surface-2 px-3 py-2 text-xs text-tertiary">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>{S.greenfieldNote}</span>
            </div>

            {sections.map((section, si) => (
              <section key={si} className="rounded-md border border-border-default bg-surface-2 p-3">
                {section.title && (
                  <h2 className="mb-3 text-sm font-medium text-secondary">{section.title}</h2>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {section.fields.map((f) => (
                    <FieldControl
                      key={f.key}
                      spec={f}
                      value={values[f.key] ?? ''}
                      onChange={(v) => patch(f.key, v)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function FieldControl({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec
  value: string
  onChange: (v: string) => void
}) {
  const id = `cfg-${spec.key}`

  // Boolean → inline checkbox row (spans full width for readability).
  if (spec.type === 'bool') {
    const checked = value === 'true' || value === '1'
    return (
      <label className="flex cursor-pointer items-start gap-2.5 sm:col-span-2">
        <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
        <span className="min-w-0">
          <span className="block text-md font-medium text-primary">{spec.label}</span>
          {spec.hint && <span className="mt-0.5 block text-xs text-tertiary">{spec.hint}</span>}
        </span>
      </label>
    )
  }

  const control = (() => {
    switch (spec.type) {
      case 'textarea':
        return (
          <Textarea
            id={id}
            value={value}
            placeholder={spec.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      case 'select':
        return (
          <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
            {(spec.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        )
      case 'color':
        return (
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label={spec.label}
              value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 w-10 shrink-0 cursor-pointer rounded-md border border-border-default bg-surface-1 p-0.5"
            />
            <Input
              mono
              value={value}
              placeholder="#000000"
              onChange={(e) => onChange(e.target.value)}
            />
          </div>
        )
      case 'password':
        return (
          <Input
            id={id}
            type="password"
            autoComplete="new-password"
            value={value}
            placeholder="••••••••"
            onChange={(e) => onChange(e.target.value)}
          />
        )
      case 'number':
        return (
          <Input
            id={id}
            type="number"
            mono={spec.mono}
            min={spec.min}
            max={spec.max}
            value={value}
            placeholder={spec.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )
      default:
        return (
          <Input
            id={id}
            mono={spec.mono}
            value={value}
            placeholder={spec.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )
    }
  })()

  return (
    <Field htmlFor={id} label={spec.label} hint={spec.secret ? spec.hint ?? S.secretHint : spec.hint}>
      {control}
    </Field>
  )
}
